import { Vehicle, Booking, AvailabilityWindow, VehicleFeasibility, MaintenancePeriod } from './types';

// Time in minutes to travel between cities
const CITY_TRAVEL_TIME = 300; // 5 hours between Pune and Mumbai (conservative estimate)
const BUFFER_TIME = 180; // 3 hours buffer time
const TOTAL_REPOSITION_TIME = CITY_TRAVEL_TIME + BUFFER_TIME; // 8 hours total

export function calculateVehicleFeasibility(
  vehicle: Vehicle,
  bookings: Booking[],
  maintenancePeriods: MaintenancePeriod[],
  availabilityWindow: AvailabilityWindow
): VehicleFeasibility {
  const reasons: string[] = [];
  let status: 'available' | 'warning' | 'unavailable' = 'available';
  
  const tripIntent = availabilityWindow.tripIntent;
  const vehicleBookings = bookings.filter(b => b.vehicleId === vehicle.id);
  
  // Note: We don't mark vehicles as unavailable for type mismatch
  // The UI will show different indicators (green vs amber) based on type match
  // All vehicles should be evaluated for actual availability (conflicts, location, etc.)
  
  // Check 1: Time conflict with existing bookings
  const hasConflict = vehicleBookings.some(booking => {
    const bookingStart = booking.startTime.getTime();
    const bookingEnd = booking.endTime.getTime();
    const windowStart = availabilityWindow.startTime.getTime();
    const windowEnd = availabilityWindow.endTime.getTime();
    
    // Check for overlap
    return (bookingStart < windowEnd && bookingEnd > windowStart);
  });
  
  if (hasConflict) {
    const conflictingBooking = vehicleBookings.find(booking => {
      const bookingStart = booking.startTime.getTime();
      const bookingEnd = booking.endTime.getTime();
      const windowStart = availabilityWindow.startTime.getTime();
      const windowEnd = availabilityWindow.endTime.getTime();
      return (bookingStart < windowEnd && bookingEnd > windowStart);
    });
    
    reasons.push(`Overlaps with booking ${conflictingBooking?.bookingId || conflictingBooking?.id}`);
    status = 'unavailable';
    return {
      vehicle,
      status,
      reasons,
      canReachInTime: false,
      hasConflict: true,
      requiresReposition: false
    };
  }
  
  // Check 2: Location and repositioning logic
  const pickupTime = availabilityWindow.startTime;
  
  // Find where the vehicle will be just before the pickup time
  const previousBookings = vehicleBookings
    .filter(b => b.endTime.getTime() <= pickupTime.getTime())
    .sort((a, b) => b.endTime.getTime() - a.endTime.getTime());
  
  const lastBooking = previousBookings[0];
  const vehicleLocation = lastBooking ? lastBooking.dropCity : vehicle.currentCity;
  const vehicleEndTime = lastBooking ? lastBooking.endTime : new Date();
  
  // Check if vehicle can reach pickup location in time
  const timeUntilPickup = (pickupTime.getTime() - vehicleEndTime.getTime()) / (1000 * 60); // in minutes
  const requiresReposition = vehicleLocation !== tripIntent.pickupCity;
  
  let canReachInTime = true;
  
  if (requiresReposition) {
    const travelTimeNeeded = TOTAL_REPOSITION_TIME; // 8 hours (5h travel + 3h buffer)
    
    if (timeUntilPickup < CITY_TRAVEL_TIME) {
      // Cannot physically make it in time (less than 5 hours travel time)
      reasons.push(`In ${vehicleLocation} – cannot reach ${tripIntent.pickupCity} by pickup time (needs ${Math.floor(CITY_TRAVEL_TIME / 60)}h)`);
      status = 'unavailable';
      canReachInTime = false;
    } else if (timeUntilPickup < travelTimeNeeded) {
      // Can make it but with insufficient buffer (between 5-8 hours)
      const bufferMinutes = Math.floor(timeUntilPickup - CITY_TRAVEL_TIME);
      reasons.push(`Tight turnaround - only ${bufferMinutes}m buffer after ${Math.floor(CITY_TRAVEL_TIME / 60)}h travel from ${vehicleLocation}`);
      status = 'warning';
    }
    // If timeUntilPickup >= 8 hours, no warning needed - sufficient time
  } else {
    // Same city, just check if there's reasonable time gap
    if (timeUntilPickup < 15 && lastBooking) {
      reasons.push(`Very tight turnaround - only ${Math.floor(timeUntilPickup)}m gap`);
      status = 'warning';
    }
  }
  
  // Check 3: Post-trip repositioning consideration
  // Only warn if the vehicle ends in a different city AND there's a next booking without sufficient repositioning time
  const tripEndCity = tripIntent.dropCity;
  const tripEndTime = availabilityWindow.endTime;
  
  // Find bookings after this trip
  const nextBookings = vehicleBookings
    .filter(b => b.startTime.getTime() > tripEndTime.getTime())
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  
  const nextBooking = nextBookings[0];
  
  if (nextBooking && tripEndCity !== nextBooking.pickupCity) {
    // Vehicle ends in different city and needs to reposition for next booking
    const timeUntilNextPickup = (nextBooking.startTime.getTime() - tripEndTime.getTime()) / (1000 * 60);
    
    if (timeUntilNextPickup < CITY_TRAVEL_TIME) {
      // Cannot reach next booking in time
      reasons.push(`Ends in ${tripEndCity} – cannot reach ${nextBooking.pickupCity} for next booking (needs ${Math.floor(CITY_TRAVEL_TIME / 60)}h)`);
      status = 'unavailable';
    } else if (timeUntilNextPickup < TOTAL_REPOSITION_TIME) {
      // Can reach but with insufficient buffer
      const bufferMinutes = Math.floor(timeUntilNextPickup - CITY_TRAVEL_TIME);
      reasons.push(`Tight turnaround after trip - only ${bufferMinutes}m buffer to reach ${nextBooking.pickupCity} for next booking`);
      if (status === 'available') {
        status = 'warning';
      }
    }
  }
  // If no next booking, or next booking has sufficient time, no warning needed
  
  // Check 4: Maintenance periods
  const hasMaintenanceConflict = maintenancePeriods.some(maintenance => {
    const maintenanceStart = maintenance.startTime.getTime();
    const maintenanceEnd = maintenance.endTime.getTime();
    const windowStart = availabilityWindow.startTime.getTime();
    const windowEnd = availabilityWindow.endTime.getTime();
    
    // Check for overlap
    return (maintenanceStart < windowEnd && maintenanceEnd > windowStart);
  });
  
  if (hasMaintenanceConflict) {
    const conflictingMaintenance = maintenancePeriods.find(maintenance => {
      const maintenanceStart = maintenance.startTime.getTime();
      const maintenanceEnd = maintenance.endTime.getTime();
      const windowStart = availabilityWindow.startTime.getTime();
      const windowEnd = availabilityWindow.endTime.getTime();
      return (maintenanceStart < windowEnd && maintenanceEnd > windowStart);
    });
    
    reasons.push(`Overlaps with maintenance period ${conflictingMaintenance?.id}`);
    status = 'unavailable';
    return {
      vehicle,
      status,
      reasons,
      canReachInTime: false,
      hasConflict: true,
      requiresReposition: false
    };
  }
  
  // If no issues found
  if (reasons.length === 0) {
    reasons.push('Available - no conflicts, vehicle in correct location');
  }
  
  return {
    vehicle,
    status,
    reasons,
    canReachInTime,
    hasConflict,
    requiresReposition
  };
}